/**
 * RecurrencePicker Component
 *
 * UI for selecting event recurrence rules with Google Calendar-style options.
 * Supports daily, weekly, monthly, yearly, and custom patterns.
 *
 * Follows single responsibility principle: only handles recurrence input UI.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';
import DatePicker from 'react-native-date-picker';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type {
  RecurrenceRule,
  RecurrenceFrequency,
  DayOfWeek,
  RecurrenceEndCondition,
} from '@/types/events';
import { RecurrenceRuleBuilder } from '@/lib/calendar/RecurrenceRuleBuilder';
import { RecurrenceOptionBuilder } from '@/lib/calendar/RecurrenceOptionBuilder';
import { CustomRecurrenceForm } from './CustomRecurrenceForm';

type RecurrencePickerProps = {
  visible: boolean;
  value: RecurrenceRule | null;
  onChange: (rule: RecurrenceRule | null) => void;
  onDismiss: () => void;
  startDate: string; // yyyy-mm-dd, used for default end date
};

type QuickOption = string; // Dynamic option ID from RecurrenceOptionBuilder

/**
 * RecurrencePicker Component
 *
 * Full-featured recurrence rule picker with quick options and custom mode.
 */
export function RecurrencePicker({
  visible,
  value,
  onChange,
  onDismiss,
  startDate,
}: RecurrencePickerProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const dynamicOptions = useMemo(
    () => RecurrenceOptionBuilder.buildOptions(startDate),
    [startDate],
  );

  const [quickOption, setQuickOption] = useState<QuickOption>('never');
  const [frequency, setFrequency] = useState<RecurrenceFrequency>('WEEKLY');
  const [interval, setInterval] = useState('1');
  const [byDay, setByDay] = useState<DayOfWeek[]>([]);
  const [byMonthDay, setByMonthDay] = useState('1');
  const [byMonth, setByMonth] = useState('1');
  const [endCondition, setEndCondition] = useState<RecurrenceEndCondition>({
    type: 'never',
  });
  const [showUntilPicker, setShowUntilPicker] = useState(false);

  // Animation values
  const backdropOpacity = useSharedValue(0);
  const translateY = useSharedValue(500);

  useEffect(() => {
    if (visible) {
      backdropOpacity.value = withTiming(1, { duration: 200 });
      translateY.value = withTiming(0, { duration: 250 });
    } else {
      backdropOpacity.value = withTiming(0, { duration: 200 });
      translateY.value = withTiming(500, { duration: 200 });
    }
  }, [visible, backdropOpacity, translateY]);

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  const contentStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  // Initialize from value
  useEffect(() => {
    if (!visible) return;

    if (!value) {
      setQuickOption('never');
      resetToDefaults();
      return;
    }

    // Find matching dynamic option
    const matchedOption = dynamicOptions.find((opt) => {
      if (opt.id === 'never' || opt.id === 'custom' || !opt.rule) return false;
      return (
        opt.rule.frequency === value.frequency &&
        opt.rule.interval === value.interval &&
        opt.rule.endCondition.type === value.endCondition.type &&
        (opt.rule.byDay ?? []).slice().sort().join(',') ===
          (value.byDay ?? []).slice().sort().join(',') &&
        opt.rule.byMonthDay === value.byMonthDay &&
        opt.rule.byMonth === value.byMonth &&
        opt.rule.bySetPos === value.bySetPos
      );
    });

    setQuickOption(matchedOption?.id ?? 'custom');

    // Populate custom fields
    setFrequency(value.frequency);
    setInterval(String(value.interval));
    setByDay(value.byDay ?? []);
    setByMonthDay(String(value.byMonthDay ?? 1));
    setByMonth(String(value.byMonth ?? 1));
    setEndCondition(value.endCondition);
  }, [visible, value, dynamicOptions]);

  const resetToDefaults = () => {
    setFrequency('WEEKLY');
    setInterval('1');
    setByDay([]);
    setByMonthDay('1');
    setByMonth('1');
    setEndCondition({ type: 'never' });
  };

  const handleQuickOptionPress = (optionId: string) => {
    setQuickOption(optionId);

    if (optionId === 'custom') {
      // Keep current custom settings
      return;
    }

    // Find the option and apply its rule
    const option = dynamicOptions.find((o) => o.id === optionId);
    if (!option) return;

    onChange(option.rule);
    onDismiss();
  };

  const handleSaveCustom = () => {
    const parsedInterval = parseInt(interval, 10);
    if (isNaN(parsedInterval) || parsedInterval < 1) {
      return;
    }

    const rule: RecurrenceRule = {
      frequency,
      interval: parsedInterval,
      endCondition,
    };

    if (frequency === 'WEEKLY' && byDay.length > 0) {
      rule.byDay = byDay;
    }

    if (frequency === 'MONTHLY') {
      const day = parseInt(byMonthDay, 10);
      if (!isNaN(day) && day >= 1 && day <= 31) {
        rule.byMonthDay = day;
      }
    }

    if (frequency === 'YEARLY') {
      const month = parseInt(byMonth, 10);
      const day = parseInt(byMonthDay, 10);
      if (!isNaN(month) && month >= 1 && month <= 12) {
        rule.byMonth = month;
      }
      if (!isNaN(day) && day >= 1 && day <= 31) {
        rule.byMonthDay = day;
      }
    }

    onChange(rule);
    onDismiss();
  };

  const toggleDay = (day: DayOfWeek) => {
    setByDay((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  const handleUntilDateConfirm = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    setEndCondition({ type: 'until', until: dateStr });
    setShowUntilPicker(false);
  };

  const getPreview = (): string => {
    if (quickOption !== 'custom') {
      const matchedOption = dynamicOptions.find((o) => o.id === quickOption);
      return matchedOption?.label ?? 'Does not repeat';
    }

    // Custom preview
    const parsedInterval = parseInt(interval, 10);
    if (isNaN(parsedInterval) || parsedInterval < 1) {
      return 'Custom...';
    }

    const rule: RecurrenceRule = {
      frequency,
      interval: parsedInterval,
      byDay: frequency === 'WEEKLY' ? byDay : undefined,
      byMonthDay:
        frequency === 'MONTHLY' || frequency === 'YEARLY'
          ? parseInt(byMonthDay, 10)
          : undefined,
      byMonth: frequency === 'YEARLY' ? parseInt(byMonth, 10) : undefined,
      endCondition,
    };

    return RecurrenceRuleBuilder.humanizeRRule(rule);
  };

  return (
    <Modal
      visible={visible}
      animationType="none"
      transparent
      onRequestClose={onDismiss}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        {/* Animated Backdrop */}
        <Animated.View style={[styles.backdrop, backdropStyle]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onDismiss} />
        </Animated.View>

        {/* Animated Content */}
        <Animated.View
          style={[styles.content, { backgroundColor: palette.background }, contentStyle]}
        >
          <View style={[styles.header, { borderBottomColor: palette.border }]}>
            <Text style={[styles.headerTitle, { color: palette.text }]}>
              Repeat
            </Text>
            <TouchableOpacity onPress={onDismiss}>
              <Ionicons name="close" size={24} color={palette.textMuted} />
            </TouchableOpacity>
          </View>

          <KeyboardAwareScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            enableOnAndroid={true}
            extraScrollHeight={20}
            enableAutomaticScroll={true}
          >
            <View style={styles.section}>
              {dynamicOptions.map((option) => (
                <QuickOptionButton
                  key={option.id}
                  label={option.label}
                  selected={quickOption === option.id}
                  onPress={() => handleQuickOptionPress(option.id)}
                  palette={palette}
                />
              ))}
            </View>

            {quickOption === 'custom' && (
              <>
                <CustomRecurrenceForm
                  frequency={frequency}
                  interval={interval}
                  byDay={byDay}
                  byMonthDay={byMonthDay}
                  byMonth={byMonth}
                  endCondition={endCondition}
                  onFrequencyChange={setFrequency}
                  onIntervalChange={setInterval}
                  onToggleDay={toggleDay}
                  onByMonthDayChange={setByMonthDay}
                  onByMonthChange={setByMonth}
                  onEndConditionChange={setEndCondition}
                  onShowUntilPicker={() => setShowUntilPicker(true)}
                  palette={palette}
                />

                {/* Preview */}
                <View
                  style={[styles.preview, { backgroundColor: palette.backgroundLight }]}
                >
                  <Ionicons name="repeat" size={20} color={palette.tint} />
                  <Text style={[styles.previewText, { color: palette.text }]}>
                    {getPreview()}
                  </Text>
                </View>

                <TouchableOpacity
                  onPress={handleSaveCustom}
                  style={[styles.saveButton, { backgroundColor: palette.tint }]}
                >
                  <Text style={styles.saveButtonText}>Save Custom Rule</Text>
                </TouchableOpacity>
              </>
            )}
          </KeyboardAwareScrollView>
        </Animated.View>
      </View>

      <DatePicker
        modal
        open={showUntilPicker}
        date={
          endCondition.type === 'until'
            ? new Date(`${endCondition.until}T00:00:00`)
            : new Date(`${startDate}T00:00:00`)
        }
        mode="date"
        onConfirm={handleUntilDateConfirm}
        onCancel={() => setShowUntilPicker(false)}
      />
    </Modal>
  );
}

type QuickOptionButtonProps = {
  label: string;
  selected: boolean;
  onPress: () => void;
  palette: typeof Colors.light;
};

function QuickOptionButton({
  label,
  selected,
  onPress,
  palette,
}: QuickOptionButtonProps) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[
        styles.quickOption,
        {
          backgroundColor: selected ? palette.tint : palette.backgroundLight,
          borderColor: selected ? palette.tint : palette.border,
        },
      ]}
    >
      <Text
        style={[
          styles.quickOptionText,
          { color: selected ? '#FFF' : palette.text },
        ]}
      >
        {label}
      </Text>
      {selected && <Ionicons name="checkmark" size={20} color="#FFF" />}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    height: '85%',
    borderTopLeftRadius: BorderRadius.xl,
    borderTopRightRadius: BorderRadius.xl,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Spacing.xl,
    paddingVertical: Spacing.lg,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: Spacing.xl,
    paddingBottom: 40, // intentionally large for scroll padding
  },
  section: {
    paddingVertical: Spacing.lg,
    gap: Spacing.md,
  },
  quickOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md + 2, // 14px
    borderRadius: BorderRadius.md,
    borderWidth: 1,
  },
  quickOptionText: {
    fontSize: 14,
    fontWeight: '500',
  },
  preview: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md + 2, // 14px
    borderRadius: BorderRadius.md,
    marginTop: Spacing.xl,
  },
  previewText: {
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  saveButton: {
    paddingVertical: Spacing.md + 2, // 14px
    borderRadius: BorderRadius.md,
    alignItems: 'center',
    marginTop: Spacing.md,
  },
  saveButtonText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
});
